import React, { useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import debounce from 'lodash.debounce';
import config from 'utils/config';
import { useOutsideClick } from 'utils/hooks/useOutsideClick';

import Loqate from './utils/loqate';
import { ADDRESS_TYPE } from './constants/index';
import {
  AddressContainer,
  ListContainer,
  ListItem,
  StyledAddressInput,
  Errors,
  HorizontalLine,
  ManuallyButton,
  BoldText
} from './AddressInput.styles';
import { getErrorKey } from './utils/error';

const AddressInput = ({ placeholder = '', onSelect = () => {}, intl }) => {
  const loqate = useMemo(() => Loqate.create(config('LOQATE_API_KEY')), []);

  const containerRef = useRef(null);

  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const find = useCallback(
    async (text, containerId) => {
      try {
        const { data } = await loqate.find({
          text,
          containerId,
          language: intl.locale?.split?.('-')?.[0]
        });

        if (data?.Items) {
          return data.Items;
        }

        return [];
      } catch (error) {
        return [{ Error: '-1' }];
      }
    },
    [intl.locale, loqate]
  );

  const debouncedHandler = useMemo(
    () =>
      debounce(async (value) => {
        if (value === '') {
          setSuggestions([]);

          return;
        }

        const suggestionsItems = await find(value);
        if (!suggestionsItems.length) {
          return;
        }

        setSuggestions(suggestionsItems);
      }, 300),
    [find]
  );

  const handleSelectSuggestion = async ({ Type, Id }) => {
    try {
      if (Type === ADDRESS_TYPE) {
        const { data = {} } = await loqate.retrieve(Id);
        const { Items = [] } = data;
        if (Items.length) {
          setSuggestions([]);
        }
        onSelect(Items[0]);
        return;
      }

      const suggestionsItems = await find(searchValue, Id);
      setSuggestions(suggestionsItems);
    } catch (error) {
      setSuggestions([{ Error: '-1' }]);
    }
  };
  const handleChange = (event) => {
    const value = event.target?.value || '';
    setSearchValue(value);
    debouncedHandler(value);
  };
  const handleSuggestionClick = (suggestion) => () => handleSelectSuggestion(suggestion);
  const handleManuallyClick = () => onSelect(null);
  const handleOutsideClick = () => {
    setSuggestions([]);
  };
  const handleFocus = () => {
    if (suggestions.length === 0) {
      debouncedHandler(searchValue);
    }
  };

  useOutsideClick(containerRef, handleOutsideClick);

  const error = suggestions.length > 0 && suggestions[0]?.Error ? getErrorKey(suggestions[0].Error) : '';
  const showHorizontalLine = suggestions.length > 0 && !suggestions[0]?.Error;

  return (
    <AddressContainer ref={containerRef} data-cy="input-address-container" data-testid="input-address-container">
      <StyledAddressInput
        value={searchValue}
        onChange={handleChange}
        placeholder={placeholder}
        onFocus={handleFocus}
        data-cy="input-of-address"
        data-testid="input-of-address"
      />
      {suggestions.length !== 0 && (
        <ListContainer data-cy="input-address-list" data-testid="input-address-list">
          <div>
            {error ? (
              <Errors data-testid="input-address-error">
                <FormattedMessage id={error} />
              </Errors>
            ) : (
              <FormattedMessage id="addressInput.continueTyping" />
            )}
            <ManuallyButton onClick={handleManuallyClick} data-cy="manually-address-button">
              <FormattedMessage id="addressInput.enterManually" />
            </ManuallyButton>
          </div>
          {showHorizontalLine && <HorizontalLine />}
          {suggestions.map((suggestion, index) =>
            !suggestion?.Error ? (
              <ListItem
                key={`${suggestion?.Id}-$loqate-data_${index}`}
                onClick={handleSuggestionClick(suggestion)}
                data-cy={`input-address-item-${suggestion.Id}`}
                data-testid={`input-address-item-${suggestion.Id}`}
              >
                <BoldText>{suggestion.Text}</BoldText> {suggestion.Description}
              </ListItem>
            ) : null
          )}
        </ListContainer>
      )}
    </AddressContainer>
  );
};

AddressInput.propTypes = {
  onSelect: PropTypes.func,
  intl: PropTypes.shape({ locale: PropTypes.string }),
  placeholder: PropTypes.string
};

export default injectIntl(AddressInput);
